import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";

import "./style.scss";
import config from "../../config";

const backendUrl = config.backend_url;

// ✅ Your dump-sync endpoints (add more here later)
const DUMP_APIS = [
  {
    key: "samsung_product_tags",
    title: "Product Tags Sync",
    subtitle: "Adds missing tags to existing products by _id or product code.",
    endpoint: "/dump-sync/samsung-products/tags/upload",
    downloadEndpoint: "/dump-sync/products/tags/list",
    accept: [".csv"],
    hint: "CSV only. Required: _id or code, plus tag columns like tag, tags[0], tags[1]. _id is best for non-Samsung products.",
    templateHeaders: ["_id", "code", "tag", "tags[1]", "tags[2]"],
    templateRows: [
      ["69a5424ab76108b18777a902", "", "4G", "", ""],
      ["69a5424ab76108b18777a95f", "", "Flagship 5G", "5G", ""],
      ["", "SM-A366EZKK", "Innovative 5G", "5G", ""],
    ],
  },
  {
    key: "user_field_sync",
    title: "User Field Sync",
    subtitle:
      "Updates existing users by code; unknown columns are added as custom user fields.",
    endpoint: "/dump-sync/users/fields/upload",
    accept: [".csv", ".xlsx", ".xls"],
    hint:
      "Required header: code. Add any user field as a column. Arrays: use labels[] with values like Top Dealer|D To D or [\"Top Dealer\",\"D To D\"]. Empty cells are ignored.",
    templateHeaders: [
      "code",
      "name",
      "status",
      "role",
      "position",
      "category",
      "district",
      "taluka",
      "town",
      "labels[]",
      "owner_details.phone",
      "credit_limit",
      "custom_note",
    ],
    templateRows: [
      [
        "RAJD015735",
        "Unique Mobile Gallery",
        "active",
        "dealer",
        "dealer",
        "DCM",
        "Jaipur",
        "WALL CITY",
        "Kishangarh",
        "Top Dealer|D To D",
        "9876543210",
        "0",
        "Any new column becomes a user field",
      ],
      [
        "RAJD018152",
        "Shree Shyam Telecom",
        "active",
        "dealer",
        "dealer",
        "DCM",
        "Jaipur",
        "",
        "",
        "[\"Market Coverage\",\"Jaipur\"]",
        "",
        "5000",
        "JSON arrays also work",
      ],
    ],
    guide: [
      "The code column matches User.code.",
      "Existing fields are updated; unknown headers are added as new fields on that user.",
      "Use [] in the header for arrays, for example labels[]. Pipe-separated values are easiest in CSV: Top Dealer|D To D.",
      "Use dot columns for nested fields, for example owner_details.phone.",
      "Empty cells do not overwrite existing data. Use null or __NULL__ only when you intentionally want null.",
      "Protected fields are skipped: code, password, _id, __v, createdAt, updatedAt.",
    ],
  },
  {
    key: "product_simple_sync",
    title: "Product Master Simple Sync",
    subtitle:
      "Creates or updates products and tags in one upload.",
    endpoint: "/dump-sync/products/simple/upload",
    accept: [".csv", ".xlsx", ".xls"],
    hint:
      "Required headers: brand, product_category, price, plus product_name or model_code/product_code. Add one or more tag columns to sync product tags.",
    templateHeaders: [
      "brand",
      "model_code",
      "product_code",
      "product_name",
      "product_category",
      "price",
      "tag",
      "tag",
      "tag",
    ],
    templateRows: [
      ["sample rows", "CODE01", "SM-CODE01", "name1", "smart_phone", "120000", "4G", "", ""],
      ["samsung", "CODE02", "SM-CODE02", "name2", "smart_phone", "120000", "5G", "Innovative 5G", ""],
      ["samsung", "CODE03", "SM-CODE03", "name3", "smart_phone", "120000", "5G", "Flagship 5G", ""],
      ["samsung", "CODE04", "SM-CODE04", "name4", "tab", "10000", "tab", "", ""],
      ["samsung", "CODE05", "SM-CODE05", "name5", "wearable", "2000", "wearable", "", ""],
    ],
    guide: [
      "Download the format, remove all sample rows, then paste or insert your actual product data.",
      "Only brand, product_category and price are required when model_code or product_code is available; product_name is recommended.",
      "Brand is saved in lowercase automatically.",
      "Every column named tag is saved into the product tags list. Add more tag columns when a product needs multiple tags.",
      "Segment is calculated from price: 0-6, 6-10, 10-20, 20-30, 30-40, 40-70, 70-100, 100-120 or 120.",
      "Leave model_code blank when it is not available.",
      "When product_code is blank, model_code is used as the product code so sales reports can match it.",
      "If both product_code and model_code are blank, the system generates a stable product code from brand and product name.",
      "Dry Run previews inserts and updates. Turn Dry Run OFF only after checking the result.",
    ],
  },
  {
    key: "samsung_products",
    title: "Samsung Dump → Product Master",
    subtitle: "Creates only missing products (brand+samsung, product_code).",
    endpoint: "/dump-sync/samsung-products/upload",
    accept: [".xlsx", ".xls", ".csv"],
    hint: "Headers needed: MarketName, ProductCode, Modelcode, Segment, Category, Price Per Unit, Segment New, Price Band",
  },
  {
    key: "mdd_dealer_sync",
    title: "Dump → ActorCodes (MDD + Dealer)",
    subtitle: "Creates/updates missing ActorCodes for mdd + dealer, then syncs Users from ActorCodes.",
    endpoint: "/admin/sync-mdd-dealer-from-dump", // <-- put your route here
    accept: [".csv"], // since you said no xlsx
    hint: "Headers used: MDD Code, MDD Name, Buyer Type, BuyerCode, BuyerName",
    },
    {
      key: "hierarchy_sync",
      title: "Dump → Default Sales Flow Hierarchy",
      subtitle:
        "Creates only missing hierarchy entries for dealers not already present in default_sales_flow.",
      endpoint: "/admin/sync-heirarchy-from-dump",
      accept: [".csv", ".xlsx", ".xls"],
      hint:
        "Headers used: SPD Code, MDD Code, Buyer Type, BuyerCode, ABM/RSO or ABM, ZSM",
    }
];

function DumpSyncUpload() {
  const [selectedApiKey, setSelectedApiKey] = useState(DUMP_APIS[0].key);
  const selectedApi = useMemo(
    () => DUMP_APIS.find((x) => x.key === selectedApiKey),
    [selectedApiKey]
  );

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // array of arrays
  const [loading, setLoading] = useState(false);

  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);

  const fileRef = useRef();

  const downloadTemplate = async () => {
    if (!selectedApi?.templateHeaders?.length) return;

    if (selectedApi.key === "product_simple_sync") {
      const xlsxStyleModule = await import("xlsx-js-style");
      const XLSXStyle = xlsxStyleModule.default || xlsxStyleModule;
      const rows = [
        selectedApi.templateHeaders,
        ...(selectedApi.templateRows || []),
      ];
      const worksheet = XLSXStyle.utils.aoa_to_sheet(rows);
      const range = XLSXStyle.utils.decode_range(worksheet["!ref"]);
      const headerStyle = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "D9D9D9" } },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
      const sampleStyle = {
        fill: { fgColor: { rgb: "F4CCCC" } },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      for (let row = range.s.r; row <= range.e.r; row += 1) {
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const address = XLSXStyle.utils.encode_cell({ r: row, c: col });
          if (!worksheet[address]) continue;
          worksheet[address].s = row === 0 ? headerStyle : sampleStyle;
        }
      }

      worksheet["!cols"] = [
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 24 },
        { wch: 20 },
        { wch: 14 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
      ];

      const workbook = XLSXStyle.utils.book_new();
      XLSXStyle.utils.book_append_sheet(workbook, worksheet, "Product Sync Format");
      XLSXStyle.writeFile(workbook, `${selectedApi.key}-format.xlsx`);
      return;
    }

    const csv = Papa.unparse({
      fields: selectedApi.templateHeaders,
      data: selectedApi.templateRows || [],
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedApi.key}-format.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // =============================
  // PREVIEW PARSERS (xlsx/csv)
  // =============================
  const parseExcelPreview = async (file) => {
    const buf = await file.arrayBuffer();
    const workbook = XLSX.read(buf, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    return json;
  };

  const parseCsvPreview = async (file) => {
    const text = await file.text();
    const parsed = Papa.parse(text, {
      header: false,
      skipEmptyLines: true,
    });
    return parsed.data;
  };

  const handleFile = async (f) => {
    if (!f) return;

    const name = (f.name || "").toLowerCase();
    const allowedExt = selectedApi.accept || [];
    const isAllowed = allowedExt.some((ext) => name.endsWith(ext.toLowerCase()));

    if (!isAllowed) {
      alert(`Invalid file type. Allowed: ${allowedExt.join(", ")}`);
      return;
    }

    setResult(null);
    setFile(f);

    try {
      let table = null;

      if (name.endsWith(".csv")) table = await parseCsvPreview(f);
      else table = await parseExcelPreview(f);

      setPreview(table);
    } catch (err) {
      console.error(err);
      setPreview(null);
      alert("Could not preview file. Please ensure file is valid.");
    }
  };

  // =============================
  // UPLOAD
  // =============================
  const uploadDump = async () => {
    if (!file) return alert("Please select a file first.");

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const url = `${backendUrl}${selectedApi.endpoint}${
        dryRun ? "?dryRun=true" : ""
      }`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Upload failed");
      }

      setResult(data);
    } catch (err) {
      console.error(err);
      alert(err.message || "Upload failed");
    }

    setLoading(false);
  };

  const downloadProductList = async () => {
    if (!selectedApi?.downloadEndpoint) return;

    setLoading(true);

    try {
      const res = await fetch(`${backendUrl}${selectedApi.downloadEndpoint}`, {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
      });

      if (!res.ok) {
        let message = "Download failed";
        try {
          const data = await res.json();
          message = data?.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || "product-tag-sync-list.csv";
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert(err.message || "Download failed");
    }

    setLoading(false);
  };

  // =============================
  // UI HELPERS
  // =============================
  const renderPreview = (data) => {
    if (!data || !data.length) return null;

    const head = data[0] || [];
    const rows = data.slice(1, 6);

    return (
      <div className="dumpsync-preview">
        <div className="dumpsync-preview__title">Preview (first 5 rows)</div>
        <div className="dumpsync-preview__tableWrap">
          <table>
            <thead>
              <tr>
                {head.map((c, i) => (
                  <th key={i}>{String(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {head.map((_, j) => (
                    <td key={j}>{row?.[j] !== undefined ? String(row[j]) : ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <div className="dumpsync-result">
        <div className="dumpsync-result__head">
          <div className="dumpsync-result__title">
            {result.dryRun ? "Dry Run Result" : "Upload Result"}
          </div>
          <div className={`dumpsync-pill ${result.success ? "ok" : "bad"}`}>
            {result.success ? "Success" : "Failed"}
          </div>
        </div>

        <div className="dumpsync-result__grid">
        {selectedApiKey === "user_field_sync" ? (
            <>
            <div className="kpi"><div className="kpi__label">Total Rows</div><div className="kpi__value">{result.totalRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Valid Rows</div><div className="kpi__value">{result.validRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Unique Codes</div><div className="kpi__value">{result.uniqueCodesInFile ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Changed Users</div><div className="kpi__value">{result.changed ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Updated Users</div><div className="kpi__value">{result.updated ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Updated Fields</div><div className="kpi__value">{result.updatedFieldChanges ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Added Fields</div><div className="kpi__value">{result.addedFieldChanges ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Not In DB</div><div className="kpi__value">{result.notFoundInDb ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Invalid Rows</div><div className="kpi__value">{result.invalidRows ?? "-"}</div></div>
            </>
        ) : selectedApiKey === "samsung_product_tags" ? (
            <>
            <div className="kpi"><div className="kpi__label">Total Rows</div><div className="kpi__value">{result.totalRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Valid Rows</div><div className="kpi__value">{result.validRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Unique Products</div><div className="kpi__value">{result.uniqueProductsInFile ?? result.uniqueCodesInFile ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Changed</div><div className="kpi__value">{result.changed ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Updated</div><div className="kpi__value">{result.updated ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Unchanged</div><div className="kpi__value">{result.unchanged ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Inactive In List</div><div className="kpi__value">{result.inactiveInList ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Not In DB</div><div className="kpi__value">{result.notFoundInDb ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">New Tags Added</div><div className="kpi__value">{result.totalNewTagsAdded ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Invalid Rows</div><div className="kpi__value">{result.invalidRows ?? "-"}</div></div>
            </>
        ) : selectedApiKey === "product_simple_sync" ? (
            <>
            <div className="kpi"><div className="kpi__label">Total Rows</div><div className="kpi__value">{result.totalRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Valid Rows</div><div className="kpi__value">{result.validRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Unique Products</div><div className="kpi__value">{result.uniqueProductsInFile ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Existing In DB</div><div className="kpi__value">{result.existingInDb ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">New To Insert</div><div className="kpi__value">{result.toInsert ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Product Updates</div><div className="kpi__value">{result.toUpdate ?? result.updated ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Products With Tags</div><div className="kpi__value">{result.productsWithTags ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Inserted</div><div className="kpi__value">{result.inserted ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Updated</div><div className="kpi__value">{result.updated ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Invalid Rows</div><div className="kpi__value">{result.invalidRows ?? "-"}</div></div>
            </>
        ) : selectedApiKey === "samsung_products" ? (
            <>
            <div className="kpi"><div className="kpi__label">Total Rows</div><div className="kpi__value">{result.totalRows ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Unique Products</div><div className="kpi__value">{result.uniqueProductsInFile ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Existing In DB</div><div className="kpi__value">{result.existingInDb ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">New To Insert</div><div className="kpi__value">{result.toInsert ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Price/Segment Updates</div><div className="kpi__value">{result.toUpdate ?? result.updated ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Inserted</div><div className="kpi__value">{result.inserted ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Updated</div><div className="kpi__value">{result.updated ?? "-"}</div></div>
            <div className="kpi"><div className="kpi__label">Skipped</div><div className="kpi__value">{result.skipped ?? "-"}</div></div>
            </>
        ) : selectedApiKey === "mdd_dealer_sync" ? (
            <>
              <div className="kpi"><div className="kpi__label">Total Rows</div><div className="kpi__value">{result.totalRows ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Unique Actors</div><div className="kpi__value">{result.uniqueActorsInFile ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Matched Existing</div><div className="kpi__value">{result.existingMatched ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Will Insert</div><div className="kpi__value">{result.willInsert ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Will Update</div><div className="kpi__value">{result.willUpdate ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Users Synced</div><div className="kpi__value">{result.usersSynced ? "Yes" : "No"}</div></div>
            </>
          ) : selectedApiKey === "hierarchy_sync" ? (
            <>
              <div className="kpi"><div className="kpi__label">Total Rows</div><div className="kpi__value">{result.totalRows ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Unique Dealers</div><div className="kpi__value">{result.uniqueDealersInFile ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Existing In Hierarchy</div><div className="kpi__value">{result.existingInHierarchy ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">To Insert</div><div className="kpi__value">{result.toInsert ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Inserted</div><div className="kpi__value">{result.inserted ?? "-"}</div></div>
              <div className="kpi"><div className="kpi__label">Skipped</div><div className="kpi__value">{result.skipped ?? "-"}</div></div>
            </>
          ) : null}
        </div>

        {Array.isArray(result.sampleUserFieldUpdates) && result.sampleUserFieldUpdates.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Sample User Field Changes</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {["code", "name", "role", "position", "field", "action", "oldValue", "newValue", "rowNumbers"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.sampleUserFieldUpdates.slice(0, 15).flatMap((user, userIndex) =>
                    (user.changes || []).map((change, changeIndex) => (
                      <tr key={`${userIndex}-${changeIndex}`}>
                        <td>{user.code}</td>
                        <td>{user.name}</td>
                        <td>{user.role}</td>
                        <td>{user.position}</td>
                        <td>{change.field}</td>
                        <td>{change.action}</td>
                        <td>{Array.isArray(change.oldValue) ? change.oldValue.join(", ") : String(change.oldValue ?? "")}</td>
                        <td>{Array.isArray(change.newValue) ? change.newValue.join(", ") : String(change.newValue ?? "")}</td>
                        <td>{Array.isArray(user.rowNumbers) ? user.rowNumbers.join(", ") : ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="dumpsync-muted">
              Dry run shows field-level changes. Turn Dry Run OFF to apply them.
            </div>
          </div>
        )}

        {Array.isArray(result.sampleChangedProducts) && result.sampleChangedProducts.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Sample Changed Products</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {["code", "product_name", "model_code", "existingTags", "addedTags", "finalTags", "status"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.sampleChangedProducts.slice(0, 10).map((product, index) => (
                    <tr key={index}>
                      <td>{product.code}</td>
                      <td>{product.product_name}</td>
                      <td>{product.model_code}</td>
                      <td>{Array.isArray(product.existingTags) ? product.existingTags.join(", ") : ""}</td>
                      <td>{Array.isArray(product.addedTags) ? product.addedTags.join(", ") : ""}</td>
                      <td>{Array.isArray(product.finalTags) ? product.finalTags.join(", ") : ""}</td>
                      <td>{product.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dumpsync-muted">Dry run shows exactly which tags will be added.</div>
          </div>
        )}

        {Array.isArray(result.notFoundProducts) && result.notFoundProducts.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Products Not Found In DB</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {["code", "rowNumbers"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.notFoundProducts.slice(0, 15).map((product, index) => (
                    <tr key={index}>
                      <td>{product.code}</td>
                      <td>{Array.isArray(product.rowNumbers) ? product.rowNumbers.join(", ") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {Array.isArray(result.notFoundUsers) && result.notFoundUsers.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Users Not Found In DB</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {["code", "rowNumbers"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.notFoundUsers.slice(0, 15).map((user, index) => (
                    <tr key={index}>
                      <td>{user.code}</td>
                      <td>{Array.isArray(user.rowNumbers) ? user.rowNumbers.join(", ") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {Array.isArray(result.inactiveProducts) && result.inactiveProducts.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Inactive Products In Upload</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {["code", "product_name", "status", "rowNumbers"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.inactiveProducts.slice(0, 15).map((product, index) => (
                    <tr key={index}>
                      <td>{product.code}</td>
                      <td>{product.product_name}</td>
                      <td>{product.status}</td>
                      <td>{Array.isArray(product.rowNumbers) ? product.rowNumbers.join(", ") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {Array.isArray(result.skippedRows) && result.skippedRows.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Skipped Rows</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {["rowNumber", "code", "reason"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.skippedRows.slice(0, 15).map((row, index) => (
                    <tr key={index}>
                      <td>{row.rowNumber}</td>
                      <td>{row.code || ""}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {Array.isArray(result.sampleNewProducts) && result.sampleNewProducts.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Sample New Products</div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {[
                      "product_name",
                      "product_code",
                      "model_code",
                      "product_category",
                      "category",
                      "price",
                      "segment",
                      "tags",
                      "source",
                      "extraction_active",
                    ].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.sampleNewProducts.slice(0, 10).map((p, i) => (
                    <tr key={i}>
                      <td>{p.product_name}</td>
                      <td>{p.product_code}</td>
                      <td>{p.model_code}</td>
                      <td>{p.product_category}</td>
                      <td>{p.category}</td>
                      <td>{p.price}</td>
                      <td>{p.segment}</td>
                      <td>{Array.isArray(p.tags) ? p.tags.join(", ") : ""}</td>
                      <td>{p.source}</td>
                      <td>{p.extraction_active}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dumpsync-muted">
              Tip: Turn off Dry Run to actually insert.
            </div>
          </div>
        )}

        {Array.isArray(result.sampleUpdates) && result.sampleUpdates.length > 0 && (
          <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">
              {selectedApiKey === "product_simple_sync"
                ? "Sample Product Updates"
                : "Sample Price / Segment Updates"}
            </div>
            <div className="dumpsync-result__tableWrap">
              <table>
                <thead>
                  <tr>
                    {[
                      "product_code",
                      "product_name",
                      "oldPrice",
                      "newPrice",
                      "oldSegment",
                      "newSegment",
                      "changedFields",
                      "rowNumbers",
                    ].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.sampleUpdates.slice(0, 10).map((item, i) => (
                    <tr key={i}>
                      <td>{item.product_code}</td>
                      <td>{item.product_name || ""}</td>
                      <td>{item.oldPrice}</td>
                      <td>{item.newPrice}</td>
                      <td>{item.oldSegment}</td>
                      <td>{item.newSegment}</td>
                      <td>{Array.isArray(item.changedFields) ? item.changedFields.join(", ") : ""}</td>
                      <td>{Array.isArray(item.rowNumbers) ? item.rowNumbers.join(", ") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dumpsync-muted">
              Dry run shows which existing products will be updated.
            </div>
          </div>
        )}

        {Array.isArray(result.sampleActors) && result.sampleActors.length > 0 && (
        <div className="dumpsync-result__sample">
            <div className="dumpsync-result__title2">Sample Actors</div>
            <div className="dumpsync-result__tableWrap">
            <table>
                <thead>
                <tr>
                    {["code", "name", "position", "role", "status"].map((h) => (
                    <th key={h}>{h}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {result.sampleActors.slice(0, 15).map((a, i) => (
                    <tr key={i}>
                    <td>{a.code}</td>
                    <td>{a.name}</td>
                    <td>{a.position}</td>
                    <td>{a.role}</td>
                    <td>{a.status}</td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>
            <div className="dumpsync-muted">Tip: Turn off Dry Run to apply changes.</div>
        </div>
        )}

        {Array.isArray(result.sampleNewHierarchyEntries) && result.sampleNewHierarchyEntries.length > 0 && (
        <div className="dumpsync-result__sample">
          <div className="dumpsync-result__title2">Sample New Hierarchy Entries</div>
          <div className="dumpsync-result__tableWrap">
            <table>
              <thead>
                <tr>
                  {["hierarchy_name", "smd", "zsm", "asm", "mdd", "tse", "dealer"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.sampleNewHierarchyEntries.slice(0, 15).map((item, i) => (
                  <tr key={i}>
                    <td>{item.hierarchy_name}</td>
                    <td>{item.smd}</td>
                    <td>{item.zsm}</td>
                    <td>{item.asm}</td>
                    <td>{item.mdd}</td>
                    <td>{item.tse}</td>
                    <td>{item.dealer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dumpsync-muted">Tip: Turn off Dry Run to apply changes.</div>
        </div>
      )}

        {result.message ? (
          <div className="dumpsync-alert">{result.message}</div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="dumpsync-page">
      <div className="dumpsync-card">
        <div className="dumpsync-header">
          <div className="dumpsync-header__left">
            <div className="dumpsync-title">📥 Dump Sync Upload</div>
              <div className="dumpsync-subtitle">
                Upload a dump file and sync products, actor codes, or hierarchy entries using the selected sync type.
              </div>
          </div>
          <div className="dumpsync-header__right">
            <label className="switch">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              <span className="slider" />
            </label>
            <div className="dumpsync-toggleText">
              Dry Run: <b>{dryRun ? "ON" : "OFF"}</b>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="dumpsync-steps">
          <div className="dumpsync-steps__title">Steps</div>
          <ul>
            <li>Upload only the file type allowed by the selected sync type.</li>
            <li>Do not rename headers.</li>
            <li>Dry Run shows what will be inserted or synced.</li>
            <li>Turn Dry Run OFF to apply actual changes.</li>
          </ul>
        </div>

        {Array.isArray(selectedApi.guide) && selectedApi.guide.length > 0 ? (
          <div className="dumpsync-guide">
            <div className="dumpsync-guide__title">How this file works</div>
            <ul>
              {selectedApi.guide.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* API Select */}
        <div className="dumpsync-controls">
          <div className="field">
            <label>Dump Sync Type</label>
            <select
              value={selectedApiKey}
              onChange={(e) => {
                setSelectedApiKey(e.target.value);
                setFile(null);
                setPreview(null);
                setResult(null);
              }}
            >
              {DUMP_APIS.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.title}
                </option>
              ))}
            </select>
            <div className="help">{selectedApi.subtitle}</div>
          </div>

          <div className="field">
            <label>File</label>

            <input
              type="file"
              hidden
              ref={fileRef}
              accept={selectedApi.accept.join(",")}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />

            <div
              className={`dropzone ${file ? "hasFile" : ""}`}
              onClick={() => fileRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <div className="dropzone__icon">📄</div>
              <div className="dropzone__text">
                {file ? (
                  <>
                    <div className="dropzone__name">{file.name}</div>
                    <div className="dropzone__meta">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </>
                ) : (
                  <>
                    <div className="dropzone__name">Drag & Drop or Click</div>
                    <div className="dropzone__meta">
                      Allowed: {selectedApi.accept.join(" , ")}
                    </div>
                  </>
                )}
              </div>

              {file ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setPreview(null);
                    setResult(null);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>

            <div className="help">{selectedApi.hint}</div>
          </div>
        </div>

        {/* Preview */}
        {renderPreview(preview)}

        {/* Actions */}
        <div className="dumpsync-actions">
          {selectedApi.templateHeaders?.length ? (
            <button
              className="btn btn-secondary"
              onClick={downloadTemplate}
              disabled={loading}
            >
              Download Format
            </button>
          ) : null}

          {selectedApi.downloadEndpoint ? (
            <button
              className="btn btn-secondary"
              onClick={downloadProductList}
              disabled={loading}
            >
              Download Product List
            </button>
          ) : null}

          <button
            className="btn btn-primary"
            onClick={uploadDump}
            disabled={loading || !file}
          >
            {loading ? "Uploading..." : dryRun ? "🧪 Run Dry Sync" : "🚀 Sync Now"}
          </button>

          <button
            className="btn btn-ghost"
            onClick={() => {
              setFile(null);
              setPreview(null);
              setResult(null);
              if (fileRef.current) fileRef.current.value = "";
            }}
            disabled={loading}
          >
            Reset
          </button>
        </div>

        {/* Result */}
        {renderResult()}
      </div>
    </div>
  );
}

export default DumpSyncUpload;
